import type { ReactNode } from "react";

/**
 * A few colours for code samples: JSON tokens and HTTP header names. Pure, so
 * the server render and hydration produce the same spans.
 */

export type CodeLanguage = "json" | "shell" | "javascript" | "python" | "http" | "text";

const KEY = "text-[#8fcdf5]";
const STRING = "text-[#b9dc7c]";
const NUMBER = "text-[#f0a37c]";
const KEYWORD = "text-[#c3a6ff]";
const MUTED = "text-[#8f98a0]";

const JSON_TOKEN = /("(?:[^"\\\n]|\\.)*")(\s*:)?|(-?\d+(?:\.\d+)?(?:[eE][+-]?\d+)?)|\b(true|false|null)\b/g;

export function highlightJson(code: string): ReactNode[] {
  const out: ReactNode[] = [];
  let last = 0;
  let key = 0;
  for (const m of code.matchAll(JSON_TOKEN)) {
    const start = m.index ?? 0;
    if (start > last) out.push(code.slice(last, start));
    if (m[1] !== undefined) {
      out.push(
        <span key={key++} className={m[2] !== undefined ? KEY : STRING}>
          {m[1]}
        </span>,
      );
      if (m[2] !== undefined) out.push(m[2]);
    } else if (m[3] !== undefined) {
      out.push(
        <span key={key++} className={NUMBER}>
          {m[3]}
        </span>,
      );
    } else {
      out.push(
        <span key={key++} className={KEYWORD}>
          {m[4]}
        </span>,
      );
    }
    last = start + m[0].length;
  }
  if (last < code.length) out.push(code.slice(last));
  return out;
}

/** `Name: value` lines (and an HTTP status line) with the names coloured. */
export function highlightHttp(code: string): ReactNode[] {
  return code.split("\n").flatMap((line, i) => {
    const nl = i > 0 ? ["\n"] : [];
    const header = /^([A-Za-z0-9-]+)(:)(.*)$/.exec(line);
    if (header) {
      return [
        ...nl,
        <span key={`k${i}`} className={KEY}>
          {header[1]}
        </span>,
        <span key={`c${i}`} className={MUTED}>
          {header[2]}
        </span>,
        header[3],
      ];
    }
    if (/^HTTP\//.test(line)) {
      return [
        ...nl,
        <span key={`s${i}`} className={KEYWORD}>
          {line}
        </span>,
      ];
    }
    return [...nl, line];
  });
}

export function highlight(code: string, language: CodeLanguage): ReactNode {
  if (language === "json") return highlightJson(code);
  if (language === "http") return highlightHttp(code);
  return code;
}
